<script setup>
/**
 * 复刻 Ionic 4 的 <ion-refresher> + <ion-refresher-content>（下拉刷新）。
 *
 * 逆向来源（旧版打包产物）：
 *   web/138.4b107ca08a15134ac67f.js  ion-refresher / ion-refresher-content 的逻辑与共用 CSS
 *   web/145.e87fcf7f000a9121d248.js  md 模式的配色（.refresher-md，文字与 spinner 都是 #000）
 *   web/101.6015405d6f9d3d2f14de.js  ion-spinner 的 circles 配置与 CSS
 *   web/svg/md-arrow-dropdown.svg    pullingIcon="arrow-dropdown" 的实际图形
 *
 * 旧版牌九账单/统计弹窗的用法：
 *   <ion-refresher (ionRefresh)="doRefresh($event)">
 *     <ion-refresher-content pullingIcon="arrow-dropdown" pullingText="下拉刷新"
 *                            refreshingSpinner="circles" refreshingText="下拉刷新...">
 *
 * 关键参数全部照抄旧版实例字段：
 *   pullMin = 60、pullMax = pullMin + 60 = 120
 *   closeDuration = '280ms'、snapbackDuration = '280ms'
 *   complete() → close(completing, '120ms')；cancel() → close(cancelling, '')
 *   close() 里 600ms 后把 state 归位 inactive
 *   手势 threshold = 20（Ionic gesture 的 threshold，位移不到 20px 不激活）
 *
 * 结构上旧版的 ion-refresher 是 ion-content 的 slot="fixed"，不随滚动；
 * 真正被 translateY 平移的是 ion-content 内部的 scrollEl。这里同样把
 * refresher 放在滚动容器外层、z-index:-1 藏在滚动容器背后，靠平移滚动容器露出来。
 */
import { computed, ref } from 'vue'

const props = defineProps({
  pullingText: { type: String, default: '' },
  refreshingText: { type: String, default: '' },
  // 目前只用到 circles，保留参数是为了跟旧版 refreshingSpinner 对齐
  refreshingSpinner: { type: String, default: 'circles' },
  disabled: { type: Boolean, default: false },
})
const emit = defineEmits(['refresh'])

// 旧版 state 是位标志：1 inactive / 2 pulling / 4 ready / 8 refreshing / 16 cancelling / 32 completing
const STATE = { inactive: 1, pulling: 2, ready: 4, refreshing: 8, cancelling: 16, completing: 32 }
const PULL_MIN = 60
const PULL_MAX = PULL_MIN + 60
const CLOSE_DURATION = '280ms'
const SNAPBACK_DURATION = '280ms'
const THRESHOLD = 20

const scrollEl = ref(null)
const state = ref(STATE.inactive)

let startY = 0
let gestureActive = false
let didStart = false
let appliedStyles = false
let closeTimer = null

// 旧版 hostData：display 由 .refresher-active 控制，其余 class 决定内部两块的显隐
const hostClass = computed(() => ({
  'refresher-active': state.value !== STATE.inactive,
  'refresher-pulling': state.value === STATE.pulling,
  'refresher-ready': state.value === STATE.ready,
  'refresher-refreshing': state.value === STATE.refreshing,
  'refresher-cancelling': state.value === STATE.cancelling,
  'refresher-completing': state.value === STATE.completing,
}))

/**
 * ion-spinner name="circles" 的 8 个圆点。
 * 旧版配置：{dur:1000, circles:8, fn:(dur,i,total)=>{
 *   step=i/total; delay=dur*step-dur; angle=2*PI*step;
 *   return {r:5, style:{top:9*sin(angle)px, left:9*cos(angle)px, animation-delay:delay}}
 * }}
 * 每个圆点是一个独立 <svg viewBox="0 0 64 64">，内含 <circle transform="translate(32,32)" r="5">。
 */
const spinnerCircles = computed(() => {
  const duration = 1000
  const total = 8
  const items = []
  for (let index = 0; index < total; index += 1) {
    const step = index / total
    const angle = 2 * Math.PI * step
    items.push({
      r: 5,
      style: {
        top: `${9 * Math.sin(angle)}px`,
        left: `${9 * Math.cos(angle)}px`,
        animationDelay: `${duration * step - duration}ms`,
        animationDuration: `${duration}ms`,
      },
    })
  }
  return items
})

/** 旧版 setCss(y, duration, overflowHidden, delay)：改的是 scrollEl 的行内样式 */
function setCss(y, duration, overflowHidden, delay) {
  appliedStyles = y > 0
  const element = scrollEl.value
  if (!element) return
  element.style.transform = y > 0 ? `translateY(${y}px) translateZ(0px)` : 'translateZ(0px)'
  element.style.transitionDuration = duration
  element.style.transitionDelay = delay
  element.style.overflow = overflowHidden ? 'hidden' : ''
}

/** 旧版 close(state, delay) */
function close(nextState, delay) {
  if (closeTimer) window.clearTimeout(closeTimer)
  closeTimer = window.setTimeout(() => {
    state.value = STATE.inactive
    didStart = false
    setCss(0, '0ms', false, '')
  }, 600)
  state.value = nextState
  setCss(0, CLOSE_DURATION, true, delay)
}

/** 旧版 complete()：close(completing, '120ms')，由业务方在数据加载完后调用 */
function complete() { close(STATE.completing, '120ms') }
/** 旧版 cancel()：close(cancelling, '') */
function cancel() { close(STATE.cancelling, '') }

/** 旧版 beginRefresh()：停在 pullMin 处，抛 ionRefresh，事件里带 complete 回调 */
function beginRefresh() {
  state.value = STATE.refreshing
  setCss(PULL_MIN, SNAPBACK_DURATION, true, '')
  emit('refresh', { complete })
}

/** 旧版 canStart()：scrollTop 必须为 0，且当前处于 inactive */
function canStart() {
  if (props.disabled) return false
  const element = scrollEl.value
  return Boolean(element) && state.value === STATE.inactive && element.scrollTop <= 0
}

function onTouchStart(event) {
  if (event.touches.length > 1) return
  if (!canStart()) { gestureActive = false; return }
  gestureActive = true
  startY = event.touches[0].clientY
  didStart = false
}

function onTouchMove(event) {
  if (!gestureActive) return
  const element = scrollEl.value
  if (!element) return
  // 旧版 onMove：多指、或已进入 refreshing/cancelling/completing 就不处理
  if (event.touches.length > 1) return
  if ((STATE.refreshing | STATE.cancelling | STATE.completing) & state.value) return

  const deltaY = event.touches[0].clientY - startY

  // 旧版 deltaY<=0 时归零并撤销行内样式
  if (deltaY <= 0) {
    state.value = STATE.inactive
    if (appliedStyles) setCss(0, '', false, '')
    return
  }
  // Ionic gesture 的 threshold=20：位移不到 20px 手势还没激活
  if (deltaY < THRESHOLD) return

  if (state.value === STATE.inactive) {
    if (element.scrollTop > 0) return
    state.value = STATE.pulling
  }

  if (event.cancelable) event.preventDefault()
  setCss(deltaY, '0ms', true, '')

  if (!didStart) didStart = true
  if (deltaY < PULL_MIN) state.value = STATE.pulling
  else if (deltaY > PULL_MAX) beginRefresh()
  else state.value = STATE.ready
}

function onTouchEnd() {
  if (!gestureActive) return
  gestureActive = false
  // 旧版 onEnd：ready → 刷新，pulling → 取消
  if (state.value === STATE.ready) beginRefresh()
  else if (state.value === STATE.pulling) cancel()
}

defineExpose({ complete, cancel })
</script>

<template>
  <div class="refresher-host">
    <!-- 旧版 ion-refresher，md 模式所以带 refresher-md -->
    <div class="ion-refresher refresher-md" :class="hostClass">
      <!-- 旧版 ion-refresher-content -->
      <div class="ion-refresher-content">
        <div class="refresher-pulling">
          <!-- pullingIcon="arrow-dropdown"，md 模式取的是 md-arrow-dropdown.svg -->
          <div class="refresher-pulling-icon">
            <svg class="refresher-icon" viewBox="0 0 512 512" aria-hidden="true"><path d="M128 192l128 128 128-128z" fill="currentColor" /></svg>
          </div>
          <div v-if="pullingText" class="refresher-pulling-text">{{ pullingText }}</div>
        </div>
        <div class="refresher-refreshing">
          <!-- refreshingSpinner="circles" -->
          <div class="refresher-refreshing-icon">
            <span class="ion-spinner spinner-circles">
              <svg v-for="(circle, index) in spinnerCircles" :key="index" viewBox="0 0 64 64" :style="circle.style">
                <circle transform="translate(32,32)" :r="circle.r" />
              </svg>
            </span>
          </div>
          <div v-if="refreshingText" class="refresher-refreshing-text">{{ refreshingText }}</div>
        </div>
      </div>
    </div>

    <!-- 相当于 ion-content 的 scrollEl，被 translateY 平移的就是它 -->
    <div
      ref="scrollEl"
      class="refresher-scroll"
      @touchstart.passive="onTouchStart"
      @touchmove="onTouchMove"
      @touchend="onTouchEnd"
      @touchcancel="onTouchEnd"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
.refresher-host{position:relative;flex:1;min-height:0;overflow:hidden}

/* 旧版 ion-refresher 共用 CSS，逐条照抄 */
.ion-refresher{left:0;top:0;display:none;position:absolute;width:100%;height:60px;z-index:-1}
.ion-refresher.refresher-active{display:block}
.ion-refresher-content{display:flex;flex-direction:column;justify-content:center;height:100%}
.refresher-pulling,.refresher-refreshing{display:none;width:100%}
.refresher-pulling-icon,.refresher-refreshing-icon{transform-origin:center;transition:.2s;font-size:30px;text-align:center}
.refresher-pulling-text,.refresher-refreshing-text{font-size:16px;text-align:center}
/* pulling / ready 显示「下拉刷新」那一块。旧版选择器是
   `.refresher-pulling ion-refresher-content .refresher-pulling`，
   外层那个类挂在 ion-refresher 宿主上，这里对应 .ion-refresher 自身 */
.ion-refresher.refresher-pulling .refresher-pulling,
.ion-refresher.refresher-ready .refresher-pulling{display:block}
/* ready 时箭头翻转 180° */
.ion-refresher.refresher-ready .refresher-pulling-icon{transform:rotate(180deg)}
/* cancelling 时仍显示 pulling 块，但图标缩成 0 */
.ion-refresher.refresher-cancelling .refresher-pulling{display:block}
.ion-refresher.refresher-cancelling .refresher-pulling-icon{transform:scale(0)}
/* refreshing / completing 显示 spinner 那一块，completing 时 spinner 缩成 0 */
.ion-refresher.refresher-refreshing .refresher-refreshing,
.ion-refresher.refresher-completing .refresher-refreshing{display:block}
.ion-refresher.refresher-completing .refresher-refreshing-icon{transform:scale(0)}

/* 旧版 .refresher-md 配色：文字与 spinner 都是 --ion-text-color 默认 #000 */
.refresher-md .refresher-pulling-icon,
.refresher-md .refresher-pulling-text,
.refresher-md .refresher-refreshing-icon,
.refresher-md .refresher-refreshing-text{color:#000}
.refresher-md .refresher-refreshing .spinner-circles circle{fill:#000}

/* ion-icon 用 font-size 控制尺寸，内部 svg 是 1em */
.refresher-icon{width:1em;height:1em;display:inline-block;vertical-align:middle}

/* 旧版 ion-spinner 的 CSS */
.ion-spinner{display:inline-block;position:relative;width:28px;height:28px;user-select:none}
.ion-spinner svg{left:0;top:0;position:absolute;width:100%;height:100%;transform:translateZ(0)}
.spinner-circles svg{animation:spinner-fade-out 1s linear infinite;fill:currentColor}
@keyframes spinner-fade-out{0%{opacity:1}to{opacity:0}}

/* 相当于 ion-content 的 scrollEl：不透明背景才能把 z-index:-1 的 refresher 遮住 */
.refresher-scroll{height:100%;overflow-y:auto;-webkit-overflow-scrolling:touch;background:#fff;transform:translateZ(0);transition-property:transform;transition-timing-function:ease}
</style>
